import { useEffect, useState } from "react";
import { Loader2, Printer, RefreshCw, Download, CheckCircle2, AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { qzPrinters, qzPrintRaw, getPrinter, savePrinter, testPageBase64, qzErrorText, getAutoPrintDo, setAutoPrintDo } from "@/lib/qz";

const STEPS = [
  <>Unduh & pasang <b>QZ Tray</b> (gratis) di komputer yang terhubung ke printer: <a href="https://qz.io/download/" target="_blank" rel="noreferrer">qz.io/download</a>.</>,
  <>Pasang driver <b>Epson LX-310</b> (mode ESC/P), atau buat printer Windows <b>&quot;Generic / Text Only&quot;</b> di port printer tersebut.</>,
  <>Klik <b>Unduh sertifikat</b>, lalu tutup QZ Tray dan salin file <code>override.crt</code> ke <code>C:\Program Files\QZ Tray\</code>. Jalankan QZ Tray lagi.</>,
  <>Klik <b>Cari printer</b>, pilih LX-310, simpan, lalu <b>Tes print</b>. Saat muncul dialog QZ Tray pertama kali, centang <b>Remember this decision</b> lalu <b>Allow</b> — selanjutnya cetak langsung tanpa dialog.</>,
];

export function PrinterSetup({ onClose }) {
  const [printers, setPrinters] = useState(null);
  const [sel, setSel] = useState(getPrinter());
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(null);
  const [autoDo, setAutoDo] = useState(getAutoPrintDo());

  const find = async () => {
    setStatus("connecting"); setErr("");
    try { const list = await qzPrinters(); setPrinters(list); setStatus("ok"); if (!sel && list.length) setSel(list.find((p) => /lx|epson|text/i.test(p)) || list[0]); }
    catch (e) { setStatus("error"); setErr(qzErrorText(e)); }
  };
  useEffect(() => { find(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = () => { savePrinter(sel); toast.success(sel ? `Printer dot-matrix: ${sel}` : "Printer dihapus"); };
  const test = async () => {
    if (!sel) { toast.error("Pilih printer dulu"); return; }
    setBusy("test");
    try { await qzPrintRaw(sel, testPageBase64(), "Tes printer Rasa Juara"); savePrinter(sel); toast.success("Halaman tes dikirim ke printer"); }
    catch (e) { toast.error(qzErrorText(e)); } finally { setBusy(null); }
  };
  const cert = async () => {
    setBusy("cert");
    try {
      const r = await api.get("/print/qz/override.crt", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "override.crt"; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  return (
    <Modal open onClose={onClose} className="printer-dialog" title="Printer Dot-Matrix (Epson LX-310)" subtitle="Cetak raw langsung ke printer lewat QZ Tray" testId="printer-setup-dialog"
      footer={<button className="rj-btn rj-btn-ghost" onClick={onClose} data-testid="printer-setup-close">Tutup</button>}>
      <div className={`qz-status ${status}`} data-testid="qz-status">
        {status === "connecting" && <><Loader2 size={15} className="spin" /> Menghubungkan ke QZ Tray…</>}
        {status === "ok" && <><CheckCircle2 size={15} /> QZ Tray terhubung · {printers?.length || 0} printer ditemukan</>}
        {status === "error" && <><AlertTriangle size={15} /> {err}</>}
        <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={find} disabled={status === "connecting"} data-testid="qz-find-btn"><RefreshCw size={13} /> Cari printer</button>
      </div>
      <div className="printer-row">
        <label className="rj-field">Printer untuk dokumen dot-matrix
          {printers?.length ? (
            <select className="rj-select" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="printer-select">
              <option value="">— Pilih printer —</option>
              {printers.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          ) : <input className="rj-input" value={sel} onChange={(e) => setSel(e.target.value)} placeholder="Nama printer persis seperti di Windows, mis. EPSON LX-310" data-testid="printer-name-input" />}
        </label>
        <button className="rj-btn rj-btn-green rj-btn-sm" onClick={save} data-testid="printer-save-btn"><Save size={14} /> Simpan</button>
        <button className="rj-btn rj-btn-primary rj-btn-sm" onClick={test} disabled={busy === "test"} data-testid="printer-test-btn">
          {busy === "test" ? <Loader2 size={14} className="spin" /> : <Printer size={14} />} Tes print</button>
      </div>
      <small className="fin-muted">Pilihan printer disimpan di browser komputer ini. Format: A5 landscape, 12 cpi, 35 baris per halaman.</small>
      <label className="autoprint-toggle light" data-testid="printer-autoprint-do">
        <input type="checkbox" checked={autoDo} onChange={(e) => { setAutoDo(e.target.checked); setAutoPrintDo(e.target.checked); }} data-testid="printer-autoprint-do-toggle" />
        Print DO otomatis ke printer ini saat pengiriman dicatat
      </label>
      <div className="qz-steps">
        <strong>Pemasangan (sekali per komputer)</strong>
        <ol>{STEPS.map((s, i) => <li key={i}>{s}</li>)}</ol>
        <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={cert} disabled={busy === "cert"} data-testid="qz-cert-download">
          {busy === "cert" ? <Loader2 size={14} className="spin" /> : <Download size={14} />} Unduh sertifikat (override.crt)</button>
      </div>
    </Modal>
  );
}
